import React from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { IExtractList } from "../../LeftPanel/ExtractList/redux/Types";
import { IRootState } from "../../../reducer/CombineReducer";
import { ExtractModes } from "../../LeftPanel/ExtractAdd/redux/Types";
import { getYearRange } from "../../LeftPanel/FinancialYearList/Helper/Script";
import { INotification } from "../../Notification/Index";
import { ErrorAlertSlice } from "../../ErrorAlert/Index";
import { ErrorAlertType, IErrorAlertObject } from "../../ErrorAlert/redux/Types";
import ExtractEditView from "./ExtractEditView";
import { SaveExtractDetail } from "./SaveExtract/SaveExtractDetail.logic";
import ExtractViewPage from "./ExtractViewPage";
import { CustomizedRibbonMenus } from "./RibbonMenus";
import { validateCloseEditExtract } from "./Helper";
import "./Style.scss";

interface IProps {
  /**
   * Extract details for viewing
   */
  selectedExtract: IExtractList;
  /**
   * Event to be triggered on clicking the edit button
   */
  onEditClick: () => void;
  /**
   * Event to be triggered on clicking the cancel button
   */
  onCancelClick: () => void;
  /**
   * Notification either success or failure
   */
  notification: INotification;
  /**
   * Event to be triggered on clicking the confirm delete button
   */
  onDeleteClick: () => void;
  /**
   * Event to be triggered on clicking the delete icon
   */
  onDeleteIconClick: () => void;
  /**
   * To show/hide delete alert
   */
  showAlert: boolean;
  /**
   * To show/hide export dialog
   */
  handleExportDialogOpen: () => void;
}

/**
 * Responsible for rendering the progress indicator component
 */
const ExtractDetailView: React.FC<IProps> = (props: IProps) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();

  const extractAdd = useSelector((state: IRootState) => state.extractAdd);
  const mode = extractAdd?.mode;
  const {
    selectedExtract,
    onEditClick,
    onDeleteIconClick,
    onCancelClick,
    handleExportDialogOpen
  } = props;
  const disabledEditButton = (mode === ExtractModes.EDIT);
  const yearData = getYearRange();
  const contentClassName = mode === ExtractModes.VIEW ? "ebt-extract-detail_landing-view"
    : "ebt-extract-detail_landing-content";

  /**
   * To restrict the navigation to dashboard
   */
  const onHandlerCancelButton = (): void => {
    const validateCancelEdit = validateCloseEditExtract(
      extractAdd.editExtractDetail,
      extractAdd.editPreviousExtractDetail,
      extractAdd
    );
    if (validateCancelEdit.openAlert) {
      const errorContent: IErrorAlertObject = {
        title: `${translate("CloseAlert.Title")}`,
        message: [`${translate("CloseAlert.Message")}`],
        type: ErrorAlertType.WARNING,
        actionButtonText: translate("Button.Yes"),
        cancelButtonText: translate("Button.No"),
        actionClick: () => onCancelClick()
      };
      dispatch(ErrorAlertSlice.actions.createErrorAlert(errorContent));
    } else {
      onCancelClick();
    }
  };


  return (
    <div className="ebt-extract-detail_landing">
      <div className="ebt-extract-detail_landing-editBtn" data-testid="ebt-ext-dtl-ribon-bar">
        <CustomizedRibbonMenus
          onEditClick={onEditClick}
          disabledEditButton={disabledEditButton}
          onDeleteIconClick={onDeleteIconClick}
          translate={translate}
          handleExportDialogOpen={handleExportDialogOpen}
        />
      </div>
      <div className={contentClassName}>
        {mode === ExtractModes.EDIT ?
          <ExtractEditView
            isEdit={false}
            yearsDataValue={yearData}
            newExtract={extractAdd.editExtractDetail}
          /> :
          <ExtractViewPage
            selectedExtract={selectedExtract} />
        }
      </div>
      {mode === ExtractModes.EDIT &&
        <SaveExtractDetail
          cancel={onHandlerCancelButton}
          notification={props.notification}
        />}
    </div>
  );
};

export default ExtractDetailView;
